import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '@/services/api'
import { wsService } from '@/services/websocket'

export interface User {
  id: string
  email: string
  name: string
  phone?: string
  avatar_url?: string
  created_at: string
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  fetchUser: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const data = await authApi.login(email, password)

          localStorage.setItem('niki_access_token', data.access_token)
          localStorage.setItem('niki_refresh_token', data.refresh_token)

          // Fetch user profile after login
          const user = await authApi.me()

          set({
            user: {
              id: user.id,
              email: user.email,
              name: user.full_name || user.username,
              phone: user.phone_number,
              avatar_url: user.avatar_url,
              created_at: user.created_at,
            },
            isAuthenticated: true,
            isLoading: false,
          })

          // Connect WebSocket with auth token
          wsService.connect(data.access_token)
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      logout: async () => {
        try {
          await authApi.logout()
        } catch {
          // Ignore logout API errors
        } finally {
          localStorage.removeItem('niki_access_token')
          localStorage.removeItem('niki_refresh_token')
          wsService.disconnect()
          set({ user: null, isAuthenticated: false })
        }
      },

      fetchUser: async () => {
        const token = localStorage.getItem('niki_access_token')
        if (!token) {
          set({ isAuthenticated: false, user: null })
          return
        }

        set({ isLoading: true })
        try {
          const userData = await authApi.me()
          set({
            user: {
              id: userData.id,
              email: userData.email,
              name: userData.full_name || userData.username,
              phone: userData.phone_number,
              avatar_url: userData.avatar_url,
              created_at: userData.created_at,
            },
            isAuthenticated: true,
            isLoading: false,
          })

          // Connect WebSocket with auth token
          wsService.connect(token)
        } catch {
          localStorage.removeItem('niki_access_token')
          localStorage.removeItem('niki_refresh_token')
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'niki-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
